/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import React, { useState } from "react";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/sonner";
import { PauseCircle, XCircle, Loader2, AlertTriangle, Play } from "lucide-react";
import { T } from "../../../lib/i18n";
import { AttachPicker } from "./PauseAttachments";

/* 设计部经理：申请暂停 / 申请取消（→ 销售总监 / CEO 审批）；重启项目直接执行。
   凭证附件（需求）：申请暂停项目需提供凭证（业主资金证明 / 市政政策文件 / 与业主的
   沟通记录等），必须上传至少 1 个附件；申请取消可选。附件随申请提交，审批人
   （销售总监 / CEO）可在「审批中心」查看并下载，作为批准与否的依据。 */
export const PauseCancelDialog = ({ p, api, kind, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);   // 凭证附件（已上传成功，元素含 fileId / name / size / kind）
  const [busy, setBusy] = useState(false);
  const isPause = kind === "pause";
  const isResume = kind === "resume";
  const run = async () => {
    if (!isResume && reason.trim().length < 2) return toast.error(T("请填写原因（≥2 字）"));
    // 暂停必须提供凭证附件（与后端 _request_pause_cancel 的校验保持一致，避免前后端提示不一致）
    if (isPause && files.length < 1) return toast.error(T("请上传至少 1 个凭证附件"));
    setBusy(true);
    try {
      if (isResume) { await api.post(`/design/v3/projects/${p.id}/resume`, { note: reason.trim() }); toast.success(T("项目已重启，恢复为暂停前的工作状态")); }
      else { await api.post(`/design/v3/projects/${p.id}/${isPause ? "pause-request" : "cancel-request"}`, { reason: reason.trim(), files }); toast.success(T(isPause ? "暂停申请已提交，待销售总监 / CEO 审批" : "取消申请已提交，待销售总监 / CEO 审批")); }
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const Icon = isResume ? Play : isPause ? PauseCircle : XCircle;
  const color = isResume ? "text-emerald-700" : isPause ? "text-amber-700" : "text-red-700";
  return (
    <div className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid={`dv3-${kind}-dialog`}>
      <div className="bg-white w-full max-w-md p-5 space-y-3 rounded-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className={`font-bold flex items-center gap-2 ${color}`}><Icon className="h-5 w-5" />{T(isResume ? "重启项目" : isPause ? "申请暂停项目" : "申请取消项目")}</h3>
        <p className="text-sm text-slate-700"><b>{p.address}</b><span className="text-slate-400"> · {p.projectId}</span></p>
        <ul className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-1" data-testid={`dv3-${kind}-effects`}>
          {isResume ? <>
            <li className="flex items-start gap-1"><Play className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />{T("无需审批：立即恢复为暂停前的工作状态「{0}」与目标日期，重新计入进行中统计", [p.pausePrevStatusName || "—"])}</li>
            <li>{T("• 销售总监 / CEO 将收到重启通知")}</li>
          </> : isPause ? <>
            <li className="flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />{T("需销售总监 / CEO 任一人批准后生效；批准后项目转入「暂停项目」，不再计入进行中 / 逾期 / 停滞统计")}</li>
            <li>{T("• 暂停期间数据、文件全部保留；设计部经理可随时直接重启，无需审批")}</li>
          </> : <>
            <li className="flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />{T("需销售总监 / CEO 任一人批准后生效；批准后项目转入「已取消」列表（文件只读可查，admin / CEO 可恢复）")}</li>
            <li>{T("• 批准时将自动取消未决审批 / 结构外包工单；CRM 客户与已签合同状态保持不变")}</li>
            <li>{T("• 若关联工程项目仍在设计阶段，将按取消联动配置推进至「1-Pre-construction」或交由工程部人工处理")}</li>
          </>}
        </ul>
        <label className="block text-xs text-slate-600">{T(isResume ? "重启说明（可选）" : "原因 *")}
          <textarea data-testid={`dv3-${kind}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm" placeholder={T(isResume ? "如：业主资金到位，恢复设计" : isPause ? "如：业主资金暂未到位 / 等待市政政策 / 业主出国…" : "如：业主资金问题终止合同 / 业主改由他人设计…")} />
        </label>
        {!isResume && (
          <div className="space-y-1.5">
            <p className="text-xs text-slate-600">{T("凭证附件")}{isPause ? <b className="text-red-500"> *</b> : <span className="text-slate-400">{T("（可选）")}</span>}<span className="text-slate-400">{T(" · 图片 / PDF / 视频 / 图纸 / 文档等，单个 ≤200MB")}</span></p>
            <AttachPicker api={api} files={files} setFiles={setFiles} busy={busy} testid={`dv3-${kind}-attach`} />
          </div>
        )}
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
          <Button size="sm" variant="outline" data-testid={`dv3-${kind}-cancel`} onClick={onClose}>{T("取消")}</Button>
          <Button size="sm" data-testid={`dv3-${kind}-confirm`} disabled={busy || (!isResume && reason.trim().length < 2) || (isPause && files.length < 1)} onClick={run} className={isResume ? "bg-emerald-600 hover:bg-emerald-700" : isPause ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : T(isResume ? "确认重启" : "提交申请")}</Button>
        </div>
      </div>
    </div>
  );
};
